import { useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { useLanguage } from '@/i18n/LanguageContext';
import { STOCK_REASON_CODES, type StockReasonCode } from '@/api/types';
import {
  ActionError, describeActionError, fromScaled, isWholeQty, newOpId, plainQty, signedQty, stockReasonLabel, toScaled,
} from '@/pages/Exceptions/shared';
import { ApiError } from '@/api/client';

export type StockActionMode = 'COUNT' | 'MOVE' | 'QUARANTINE' | 'WRITE_OFF';

/** One stock position - exactly one owner, one location, one product. */
export type StockTarget = {
  /** Needed only to list destination locations for MOVE. */
  readonly warehouseId?: string | null;
  readonly locationId: string;
  readonly locationCode: string;
  readonly ownerId: string;
  readonly ownerName?: string | null;
  readonly skuId: string;
  readonly skuLabel: string;
  readonly lotId?: string | null;
  /** The system quantity as the server last said it (decimal string). */
  readonly systemQty: string;
};

type Step = 'form' | 'confirm' | 'done';

const TITLE_KEY = {
  COUNT: 'stx.title.COUNT', MOVE: 'stx.title.MOVE', QUARANTINE: 'stx.title.QUARANTINE', WRITE_OFF: 'stx.title.WRITE_OFF',
} as const;
const CONFIRM_KEY = {
  COUNT: 'stx.confirmCount', MOVE: 'stx.confirmMove', QUARANTINE: 'stx.confirmQuarantine', WRITE_OFF: 'stx.confirmWriteOff',
} as const;

/**
 * The one inline panel behind every stock correction: Problem -> Action ->
 * Reason -> Confirm -> Result. Count/correct, Move, Quarantine and Write off
 * share it so the flow reads the same everywhere.
 *
 * `opId` is generated once per submission and reused if that submission is
 * retried (network failure); it changes only when the operator edits the
 * form or after a definitive STOCK.COUNT_STALE answer.
 */
export function StockActionDialog({
  mode, target, fixedReason, onClose, onDone,
}: {
  mode: StockActionMode;
  target: StockTarget;
  fixedReason?: StockReasonCode;
  onClose: () => void;
  /** Called when the operator closes the finished panel - the page reloads its data here. */
  onDone: () => void;
}): JSX.Element {
  const { t } = useLanguage();
  const uid = useId();
  const [step, setStep] = useState<Step>('form');
  const [qty, setQty] = useState('');
  const [destId, setDestId] = useState('');
  const [reason, setReason] = useState<StockReasonCode | ''>(fixedReason ?? '');
  const [note, setNote] = useState('');
  const [systemQty, setSystemQty] = useState(target.systemQty);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ main: string; extra: string[]; broken: boolean } | null>(null);
  const opId = useRef(newOpId());
  const touch = (): void => {
    opId.current = newOpId();
  };

  const structure = useApi(
    () => (mode === 'MOVE' && target.warehouseId ? endpoints.warehouseStructure(target.warehouseId) : Promise.resolve(null)),
    [mode, target.warehouseId],
  );
  const destinations =
    structure.status === 'success' && structure.data
      ? [...structure.data.cells, ...structure.data.otherLocations].filter((l) => l.id !== target.locationId)
      : [];

  const reasons = STOCK_REASON_CODES.filter((r) => mode === 'COUNT' || r !== 'FOUND');
  const destCode = destinations.find((d) => d.id === destId)?.code ?? '';
  const systemPlain = plainQty(systemQty);

  function validate(): string | null {
    if (mode === 'COUNT') {
      if (!isWholeQty(qty)) return t('stx.invalidQty');
    } else {
      if (!isWholeQty(qty) || toScaled(qty) <= BigInt(0)) return t('stx.qtyPositive');
      if (mode === 'MOVE' && !destId) return t('stx.destMissing');
    }
    if (!reason) return t('stx.reasonMissing');
    if (reason === 'OTHER' && note.trim().length < 3) return t('stx.noteMissing');
    return null;
  }

  function onContinue(): void {
    const problem = validate();
    setFormError(problem);
    setSubmitError(null);
    if (!problem) setStep('confirm');
  }

  async function submit(): Promise<void> {
    if (busy || !reason) return;
    setBusy(true);
    setSubmitError(null);
    const noteValue = note.trim() ? { note: note.trim() } : {};
    const lot = target.lotId ? { lotId: target.lotId } : {};
    try {
      if (mode === 'COUNT') {
        const res = await endpoints.countStock({
          opId: opId.current, locationId: target.locationId, ownerId: target.ownerId, skuId: target.skuId, ...lot,
          countedQty: qty.trim(), expectedQty: systemQty, reasonCode: reason, ...noteValue,
        });
        const delta = toScaled(res.delta);
        const main =
          delta === BigInt(0)
            ? t('stx.doneCountSame', { qty: plainQty(res.countedQty) })
            : t('stx.doneCount', { system: plainQty(res.oldQty), actual: plainQty(res.countedQty), delta: signedQty(delta) });
        finish(main, res.brokenReservations, res.flagsResolved);
      } else {
        const res = await endpoints.moveStock({
          opId: opId.current, kind: mode, fromLocationId: target.locationId, ownerId: target.ownerId, skuId: target.skuId, ...lot,
          ...(mode === 'MOVE' ? { toLocationId: destId } : {}),
          qty: qty.trim(), reasonCode: reason, ...noteValue,
        });
        const q = plainQty(res.qty);
        const main =
          mode === 'MOVE'
            ? t('stx.doneMove', { qty: q, from: target.locationCode, to: destCode })
            : mode === 'QUARANTINE'
              ? t('stx.doneQuarantine', { qty: q, from: target.locationCode })
              : t('stx.doneWriteOff', { qty: q, from: target.locationCode });
        finish(main, res.brokenReservations, 0);
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === 'STOCK.COUNT_STALE' && err.params.current !== undefined) {
        // Stock moved while the operator was counting: show the new system quantity and let them count again.
        const current = String(err.params.current);
        setSystemQty(current);
        setNotice(t('err.countStale', { current: plainQty(current) }));
        touch();
        setStep('form');
      } else {
        setSubmitError(describeActionError(t, err));
      }
    } finally {
      setBusy(false);
    }
  }

  function finish(main: string, broken: number, flagsResolved: number): void {
    const extra: string[] = [];
    if (broken > 0) extra.push(t('stx.doneBroken', { n: broken }));
    if (flagsResolved > 0) extra.push(t('stx.doneFlags'));
    setDone({ main, extra, broken: broken > 0 });
    setStep('done');
  }

  const confirmText = (): string => {
    if (mode === 'COUNT') {
      const delta = toScaled(qty.trim()) - toScaled(systemQty);
      return delta === BigInt(0)
        ? t('stx.confirmCountSame', { system: systemPlain, actual: fromScaled(toScaled(qty.trim())) })
        : t('stx.confirmCountText', { system: systemPlain, actual: fromScaled(toScaled(qty.trim())), delta: signedQty(delta) });
    }
    if (mode === 'MOVE') return t('stx.confirmMoveText', { qty: qty.trim(), from: target.locationCode, to: destCode });
    if (mode === 'QUARANTINE') return t('stx.confirmQuarantineText', { qty: qty.trim(), from: target.locationCode });
    return t('stx.confirmWriteOffText', { qty: qty.trim(), sku: target.skuLabel, from: target.locationCode });
  };

  const fieldId = (n: string): string => `${uid}-${n}`;
  const title = t(TITLE_KEY[mode]);

  return (
    <section className="card stock-panel" role="dialog" aria-label={title}>
      <h2 className="card-title">{title}</h2>
      <dl className="kv">
        <dt>{t('stx.fieldProduct')}</dt>
        <dd>{target.skuLabel}</dd>
        <dt>{t('stx.fieldLocation')}</dt>
        <dd>{target.locationCode}</dd>
        {target.ownerName && (
          <>
            <dt>{t('stx.fieldOwner')}</dt>
            <dd>{target.ownerName}</dd>
          </>
        )}
        <dt>{mode === 'COUNT' ? t('stx.systemQty') : t('stx.onHandQty')}</dt>
        <dd>{systemPlain}</dd>
      </dl>

      {step === 'form' && (
        <form
          className="order-create"
          onSubmit={(e) => {
            e.preventDefault();
            onContinue();
          }}
        >
          {notice && (
            <div className="notice" role="alert">
              {notice}
            </div>
          )}
          <div className="field-row">
            <label htmlFor={fieldId('qty')}>{mode === 'COUNT' ? t('stx.actualQty') : t('stx.qty')}</label>
            <input
              id={fieldId('qty')}
              className="qty-input"
              inputMode="numeric"
              autoComplete="off"
              value={qty}
              onChange={(e) => {
                setQty(e.target.value);
                touch();
              }}
            />
          </div>
          {mode === 'MOVE' && (
            <div className="field-row">
              <label htmlFor={fieldId('dest')}>{t('stx.destination')}</label>
              <select
                id={fieldId('dest')}
                value={destId}
                onChange={(e) => {
                  setDestId(e.target.value);
                  touch();
                }}
                disabled={structure.status !== 'success'}
              >
                <option value="">{structure.status === 'loading' ? t('stx.destinationLoading') : t('stx.destinationPlaceholder')}</option>
                {destinations.map((d) => (
                  <option key={d.id} value={d.id}>
                    {'zone_name' in d && d.zone_name ? `${d.zone_name} · ${d.code}` : d.code}
                  </option>
                ))}
              </select>
              {structure.status === 'error' && <ActionError message={describeActionError(t, structure.error)} />}
            </div>
          )}
          {fixedReason ? (
            <p className="field-hint">{t('stx.reasonLine', { reason: stockReasonLabel(t, fixedReason) })}</p>
          ) : (
            <div className="field-row">
              <label htmlFor={fieldId('reason')}>{t('stx.reason')}</label>
              <select
                id={fieldId('reason')}
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value as StockReasonCode | '');
                  touch();
                }}
              >
                <option value="">{t('stx.reasonPlaceholder')}</option>
                {reasons.map((r) => (
                  <option key={r} value={r}>
                    {stockReasonLabel(t, r)}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="field-row">
            <label htmlFor={fieldId('note')}>{reason === 'OTHER' ? t('stx.noteRequired') : t('stx.note')}</label>
            <textarea
              id={fieldId('note')}
              rows={2}
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
                touch();
              }}
            />
          </div>
          {formError && (
            <div className="field-error" role="alert">
              {formError}
            </div>
          )}
          <div className="form-actions">
            <button type="submit" className="primary">
              {t('stx.continue')}
            </button>
            <button type="button" onClick={onClose}>
              {t('stx.cancel')}
            </button>
          </div>
        </form>
      )}

      {step === 'confirm' && reason && (
        <div>
          <div className={`card ${mode === 'WRITE_OFF' ? 'danger-card' : 'confirm-card'}`} data-testid="stock-confirm">
            <p style={{ margin: 0, fontWeight: 600 }}>{confirmText()}</p>
            <p className="field-hint">{t('stx.reasonLine', { reason: stockReasonLabel(t, reason) })}</p>
            {note.trim() && <p className="field-hint">{t('stx.noteLine', { note: note.trim() })}</p>}
          </div>
          {submitError && <ActionError message={submitError} />}
          <div className="form-actions">
            <button
              type="button"
              className={mode === 'WRITE_OFF' ? 'danger' : 'primary'}
              onClick={() => void submit()}
              disabled={busy}
            >
              {busy ? t('stx.working') : t(CONFIRM_KEY[mode], { qty: qty.trim() })}
            </button>
            <button type="button" onClick={() => setStep('form')} disabled={busy}>
              {t('stx.back')}
            </button>
          </div>
        </div>
      )}

      {step === 'done' && done && (
        <div className="success-card card" role="status">
          <p style={{ margin: 0, fontWeight: 600 }}>{done.main}</p>
          {done.extra.map((line) => (
            <p key={line} className="field-hint">
              {line}
            </p>
          ))}
          {done.broken && <Link to="/exceptions">{t('stx.openExceptions')}</Link>}
          <div className="form-actions">
            <button type="button" className="primary" onClick={onDone}>
              {t('stx.close')}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
